<template>
  <div :class="$style.swiperContainer">
    <swiper-container ref="containerEl" init="false">
      <swiper-slide v-for="(slide, index) in slides" :key="index">
        <component :is="slide.url ? 'a' : 'div'" :href="slide.url">
          <p :class="[$style.image, slide.published_at === '' ? 'draft-content' : '']">
            <img :src="slide.image_url" :alt="slide.image_alt || ''" :width="imageWidth" :height="imageHeight" :loading="index === 0 ? 'eager' : 'lazy'" />
          </p>
          <div v-if="slide.title" :class="$style.text">
            <p v-if="slide.series_name" :class="$style.seriesTitle">{{ slide.series_name }}</p>
            <p :class="$style.title">{{ slide.title }}</p>
          </div>
        </component>
      </swiper-slide>
    </swiper-container>
    <div v-if="pagination" class="swiper-pagination" />
    <template v-if="navigation">
      <div class="swiper-button-navigation swiper-button-prev" />
      <div class="swiper-button-navigation swiper-button-next" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { register } from 'swiper/element/bundle';
import { type SwiperOptions } from 'swiper/types';
import { ref, onMounted } from 'vue';
import { type Slide } from '@/types/swiper';

const props = withDefaults(
  defineProps<{
    slides: Slide[];
    imageWidth: number;
    imageHeight: number;
    effect?: 'slide' | 'fade';
    pagination?: boolean;
    navigation?: boolean;
    spaceBetween?: number;
    slidesPerView?: number;
    slidesPerViewSp?: number;
    slidesPerGroup?: number;
  }>(),
  {
    effect: 'slide',
    pagination: true,
    navigation: true,
    spaceBetween: 0,
    slidesPerView: 1,
    slidesPerViewSp: 1,
    slidesPerGroup: 1,
  },
);
const containerEl = ref();

// register Swiper custom elements
if (props.slides.length > 1) {
  register();
}

onMounted(async () => {
  if (props.slides.length > 1) {
    const options: SwiperOptions = {
      loop: true,
      speed: 600,
      autoplay: {
        delay: 6000,
        disableOnInteraction: false,
      },
      centeredSlides: true,
      spaceBetween: props.spaceBetween,
      slidesPerView: props.slidesPerViewSp,
      slidesPerGroup: props.slidesPerGroup,
      breakpoints: {
        640: {
          slidesPerView: props.slidesPerView,
        },
      },
    };

    if (props.effect === 'fade') {
      options.effect = 'fade';
      options.speed = 1300;
      options.fadeEffect = {
        crossFade: true,
      };
    }

    if (props.pagination) {
      options.pagination = {
        el: '.swiper-pagination',
        type: 'bullets',
        clickable: true,
      };
    }

    if (props.navigation) {
      options.navigation = {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      };
    }

    if (containerEl.value) {
      Object.assign(containerEl.value, options);
      containerEl.value.initialize();
    }
  }
});
</script>

<style module lang="scss">
.swiperContainer {
  position: relative;

  a {
    display: block;
    text-decoration: none;
    transition: opacity 0.4s;

    &:hover {
      opacity: 0.8;
    }
  }
}

.image img {
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.text {
  margin-top: 12px;
}

.seriesTitle {
  color: #515151;
  font-size: 12px;
  margin-bottom: 2px;
  text-align: center;
}

.title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 5px;
  padding: 0 5%;
  text-align: center;
}

:global(.swiper-button-navigation) {
  align-items: center;
  background-color: #695948;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: 38px;
  justify-content: center;
  margin-top: -34px;
  opacity: 0.5;
  overflow: hidden;
  position: absolute;
  top: 50%;
  width: 38px;
  z-index: 10;

  @media (--sp) {
    display: none;
  }

  &:hover {
    opacity: 0.7;
  }

  &::before {
    color: var(--color-white);
    font-family: $font-icon;
    font-size: 40px;
    font-weight: bold;
    position: relative;
  }
}

:global(.swiper-button-prev) {
  left: 10px;

  &::before {
    content: $icon-arrow-left;
    left: -1px;
  }

  &::after {
    content: unset;
  }
}

:global(.swiper-button-next) {
  right: 10px;

  &::before {
    content: $icon-arrow-right;
    right: -1px;
  }

  &::after {
    content: unset;
  }
}

:global(.swiper-pagination) {
  text-align: center;
}

:global(.swiper-pagination-bullets) {
  padding: 4px 0 5px;
  -webkit-tap-highlight-color: transparent;
}

:global(.swiper-pagination-bullet) {
  background-color: var(--color-black);
  border-radius: 50%;
  cursor: pointer;
  display: inline-block;
  height: 6px;
  margin: 0 4px;
  opacity: 0.2;
  width: 6px;
}

:global(.swiper-pagination-bullet-active) {
  opacity: 0.6;
}
</style>
